// Admin API core: token management + request helpers (God Mode / Super Admin)

import { API_BASE_URL } from '../../config/api';

// Admin token management (separate from user auth)
let adminToken: string | null = localStorage.getItem('buzzbit_admin_token');

export const setAdminToken = (token: string | null) => {
  adminToken = token;
  if (token) {
    localStorage.setItem('buzzbit_admin_token', token);
  } else {
    localStorage.removeItem('buzzbit_admin_token');
  }
};

export const getAdminToken = () => adminToken;

export const clearAdminToken = () => {
  adminToken = null;
  localStorage.removeItem('buzzbit_admin_token');
};

// Check if admin is authenticated
export const isAdminAuthenticated = () => !!adminToken;

/**
 * Pull a human-readable message out of an error body.
 *
 * The server's standard envelope is `{ success: false, error: { code, message } }`,
 * so `body.error` is an OBJECT — passing it straight to `new Error()` stringifies
 * to "[object Object]", which is what every failed admin call used to show the
 * operator. Handle the envelope, the legacy `{ error: 'text' }` string form, and
 * a bare `{ message }`, in that order.
 */
function errorMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object') {
    const { error, message } = body as { error?: unknown; message?: unknown };
    if (error && typeof error === 'object') {
      const nested = (error as { message?: unknown }).message;
      if (typeof nested === 'string' && nested) return nested;
    }
    if (typeof error === 'string' && error) return error;
    if (typeof message === 'string' && message) return message;
  }
  return `HTTP ${status}`;
}

// Admin API request helper
export async function adminApiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>)
  };

  if (adminToken) {
    headers['Authorization'] = `Bearer ${adminToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/admin${endpoint}`, {
    ...options,
    headers
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));

    // Redirect to admin login on 401
    if (response.status === 401) {
      clearAdminToken();
      window.location.href = '/admin/login';
    }

    throw new Error(errorMessage(error, response.status));
  }

  const json = await response.json();
  return json.data ?? json;
}

// Envelope-preserving request helper.
//
// `adminApiRequest` above unwraps `{ success, data, meta }` down to the bare
// `data` value — dozens of callers depend on that and work correctly. But a
// handful of methods are typed as the FULL envelope and their consumers read
// `.data` / `.meta` / `.success`. For those we must return the raw envelope so
// pagination `meta` survives and `success` is present. Used ONLY by the methods
// whose consumers read the envelope; do NOT point unwrap-reliant methods here.
export async function adminApiRequestEnvelope<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>)
  };

  if (adminToken) {
    headers['Authorization'] = `Bearer ${adminToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/admin${endpoint}`, {
    ...options,
    headers
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));

    if (response.status === 401) {
      clearAdminToken();
      window.location.href = '/admin/login';
    }

    throw new Error(errorMessage(error, response.status));
  }

  return (await response.json()) as T;
}

// ============================================================================
// Shared internal types
// ============================================================================

/** Standard list response envelope from Phase 2 routers: `sendSuccess(res, rows, { meta })`. */
export interface ListResponse<T> {
  success: true;
  data: T[];
  meta?: { page: number; limit: number; total: number; pages: number };
}

export interface WorkspaceRef {
  id: string;
  name: string;
  domain: string | null;
  /** Workspace OWNER's user id — used by GhostModeButton. Null if no OWNER member. */
  ownerUserId: string | null;
}
